import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, Clock, Users, CheckCircle, AlertCircle, Link2, Wifi, WifiOff } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { useReservationUpdates } from '../hooks/useWebSocket';
import { getLocalizedField, pickLocalizedText } from '../utils/localizedContent';
import { DateTimeSelector } from '../components/reservations/ReservationForm';

export default function GameReservation() {
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [duration, setDuration] = useState(1);
  const [partySize, setPartySize] = useState(2);
  const [availability, setAvailability] = useState(null);
  const [tableSuggestion, setTableSuggestion] = useState(null);
  const [myReservations, setMyReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const { user } = useAuth();
  const { t, lang } = useLang();
  const text = (es, en) => pickLocalizedText(lang, es, en);

  const partySizeMin = selectedGame?.min_players || 1;
  const partySizeMax = Math.min(10, selectedGame?.max_players || 10);

  const loadGames = useCallback(() => {
    return axios.get(`${API}/reservations/games`)
      .then((r) => {
        const gameList = r.data || [];
        setGames(gameList);
        return gameList;
      })
      .catch(() => []);
  }, []);

  const loadMyReservations = useCallback(() => {
    if (!user) return Promise.resolve();
    return axios.get(`${API}/reservations/my`)
      .then((r) => setMyReservations(r.data || []))
      .catch(() => {});
  }, [user]);

  const loadAvailability = useCallback((gameId, date) => {
    if (!gameId || !date) return Promise.resolve();
    return axios.get(`${API}/reservations/availability`, {
      params: { game_id: gameId, date }
    }).then((r) => setAvailability(r.data)).catch(() => {});
  }, []);

  const loadTableSuggestion = useCallback((date, slot, nextDuration, nextPartySize) => {
    if (!date || !slot) {
      setTableSuggestion(null);
      return Promise.resolve();
    }
    return axios.get(`${API}/reservations/tables/suggest`, {
      params: { date, time_slot: slot, duration: nextDuration, party_size: nextPartySize }
    }).then((r) => setTableSuggestion(r.data)).catch(() => setTableSuggestion(null));
  }, []);

  // Restore pending reservation from sessionStorage after login
  useEffect(() => {
    if (!user) return;
    const pending = sessionStorage.getItem('pending_game_reservation');
    if (!pending) return;
    try {
      const data = JSON.parse(pending);
      sessionStorage.removeItem('pending_game_reservation');
      if (data.gameId) {
        // Find the game in the list and restore state
        loadGames().then((gameList) => {
          const game = gameList.find(g => g.product_id === data.gameId);
          if (game) {
            setSelectedGame(game);
            setSelectedDate(data.date || '');
            setSelectedSlot(data.slot || '');
            setDuration(data.duration || 1);
            setPartySize(data.partySize || 2);
          }
        }).catch(() => {});
      }
    } catch (err) { console.error('Failed to restore pending reservation:', err); }
  }, [loadGames, user]);

  // Handle real-time reservation updates
  const handleRealtimeUpdate = useCallback((data) => {
    if (selectedGame && selectedDate && data.data?.date === selectedDate) {
      loadAvailability(selectedGame.product_id, selectedDate);
      if (selectedSlot) {
        loadTableSuggestion(selectedDate, selectedSlot, duration, partySize);
      }
    }
    if (user) {
      loadMyReservations();
    }
  }, [duration, loadAvailability, loadMyReservations, loadTableSuggestion, partySize, selectedDate, selectedGame, selectedSlot, user]);

  const { isConnected } = useReservationUpdates(handleRealtimeUpdate);

  useEffect(() => {
    loadGames();
    loadMyReservations();
  }, [loadGames, loadMyReservations]);

  useEffect(() => {
    if (selectedGame && selectedDate) {
      loadAvailability(selectedGame.product_id, selectedDate);
    }
  }, [loadAvailability, selectedDate, selectedGame]);

  // Always get table suggestions when slot/party size changes
  useEffect(() => {
    loadTableSuggestion(selectedDate, selectedSlot, duration, partySize);
  }, [duration, loadTableSuggestion, partySize, selectedDate, selectedSlot]);

  const previewSlots = useMemo(() => {
    if (!availability?.slots?.length) return null;
    if (!selectedSlot || duration < 2) return availability.slots;
    const idx = availability.slots.findIndex((s) => s.slot === selectedSlot);
    if (idx < 0) return availability.slots;
    return availability.slots.map((s, j) => {
      if (j < idx || j >= idx + duration) return s;
      const copies = Math.max(0, (s.available_copies ?? 0) - 1);
      return {
        ...s,
        available_copies: copies,
        available: copies > 0,
      };
    });
  }, [availability, selectedSlot, duration]);

  const slotDisabled = useCallback((slot) => {
    if (!selectedGame || !previewSlots) return true;
    const s = previewSlots.find((x) => x.slot === slot);
    if (!s) return true;
    const copies = s.available_copies ?? (s.available ? availability?.max_copies ?? 0 : 0);
    return !s.available || copies <= 0;
  }, [selectedGame, previewSlots, availability]);

  const slotDetail = useCallback((slot) => {
    if (!selectedGame || !previewSlots) return '';
    const s = previewSlots.find((x) => x.slot === slot);
    if (!s) return '';
    const copies = s.available_copies ?? (s.available ? availability?.max_copies ?? 0 : 0);
    if (!s.available || copies <= 0) return text('agotado', 'full');
    return `${copies}u`;
  }, [selectedGame, previewSlots, availability, text]);

  const slotClassName = useCallback((slot) => {
    if (!previewSlots) return '';
    const s = previewSlots.find((x) => x.slot === slot);
    if (!s || !s.available) return '';
    const copies = s.available_copies ?? (s.available ? availability?.max_copies ?? 0 : 0);
    if (copies <= 0) return '';
    if (copies === 1) return 'hover:scale-105 cursor-pointer bg-[#F59E0B]/10 border-[#F59E0B]/30 text-[#F59E0B]';
    return 'hover:scale-105 cursor-pointer status-available border-[#2D8B70]/20';
  }, [previewSlots, availability]);

  const handleConfirm = async () => {
    if (!user) {
      // Save reservation state and redirect to login
      sessionStorage.setItem('pending_game_reservation', JSON.stringify({
        gameId: selectedGame?.product_id,
        date: selectedDate,
        slot: selectedSlot,
        duration,
        partySize
      }));
      window.location.href = '/auth?redirect=/reservations/games';
      return;
    }
    if (!selectedGame || !selectedDate || !selectedSlot) {
      setError(text('Completa todos los campos', 'Fill all fields'));
      return;
    }
    if (tableSuggestion && !tableSuggestion.can_accommodate) {
      setError(text('No hay mesas disponibles para el grupo', 'No tables available for the group'));
      return;
    }
    setLoading(true); setError(''); setSuccess('');
    try {
      const response = await axios.post(`${API}/reservations`, {
        game_id: selectedGame.product_id,
        date: selectedDate,
        time_slot: selectedSlot,
        duration_hours: duration,
        party_size: partySize,
        auto_reserve_table: true
      });
      const linkedTables = response.data.linked_tables?.length || 0;
      if (linkedTables > 0) {
        setSuccess(text(
          `¡Reserva confirmada! Juego + ${linkedTables} mesa(s) reservada(s).`,
          `Reservation confirmed! Game + ${linkedTables} table(s) reserved.`,
        ));
      } else {
        setSuccess(text('¡Juego reservado!', 'Game reserved!'));
      }
      setSelectedSlot(''); setSelectedGame(null);
      await loadMyReservations();
      setAvailability(null);
      setTableSuggestion(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
    }
    setLoading(false);
  };

  const handleCancel = async (id) => {
    try {
      await axios.delete(`${API}/reservations/${id}`);
      setMyReservations(prev => prev.map(r => r.reservation_id === id ? { ...r, status: 'cancelled' } : r));
    } catch (err) {
      console.error('Failed to cancel reservation:', err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 page-enter">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-4xl font-black text-[#F5F0E6]" style={{ fontFamily: 'Cinzel, serif' }}>{t('reservations.game_title')}</h1>
        {/* Real-time connection indicator */}
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${isConnected ? 'bg-[#2D8B70]/20 text-[#2D8B70]' : 'bg-[#C45B4A]/20 text-[#F87171]'}`}>
          {isConnected ? <Wifi size={12} /> : <WifiOff size={12} />}
          {isConnected ? t('reservations.live') : t('reservations.offline')}
        </div>
      </div>
      <p className="text-[#A8B5AE] mb-8">{text('Reserva un juego de mesa para jugar en nuestra sala. La mesa se reserva automáticamente.', 'Reserve a board game to play in our lounge. Table is automatically booked.')}</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Game Selection */}
        <div className="space-y-6">
          <div>
            <h3 className="text-[#F5F0E6] font-bold mb-3 text-sm uppercase tracking-wider text-[#C9A962]">{t('reservations.select_game')}</h3>
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
              {games.map(game => (
                <button key={game.product_id} onClick={() => { setSelectedGame(game); setSelectedSlot(''); setAvailability(null); setPartySize(game.min_players || 2); }}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${selectedGame?.product_id === game.product_id ? 'border-[#C9A962] bg-[#C9A962]/10' : 'border-[#C9A962]/20 bg-[#0F1F1A] hover:border-[#C9A962]/40'}`}
                  data-testid={`game-select-${game.product_id}`}>
                  <img src={game.images?.[0]} alt={game.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[#F5F0E6] font-semibold text-sm truncate">{getLocalizedField(game, 'name', lang, game.name)}</p>
                    <div className="flex items-center gap-2 text-xs text-[#A8B5AE]">
                      <span><Users size={10} className="inline mr-1" />{game.min_players}-{game.max_players}</span>
                      <span>{game.available_for_rent} {t('common.available_copies')}</span>
                    </div>
                  </div>
                  {selectedGame?.product_id === game.product_id && <CheckCircle size={16} className="text-[#C9A962] flex-shrink-0" />}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Middle: same date / hour / duration / party UI as table reservations */}
        <div className="space-y-6">
          <DateTimeSelector
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            selectedSlot={selectedSlot}
            setSelectedSlot={setSelectedSlot}
            duration={duration}
            setDuration={setDuration}
            partySize={partySize}
            setPartySize={setPartySize}
            setSelectedTables={() => {}}
            t={t}
            lang={lang}
            waitForSlotsEnabled
            slotsEnabled={!!selectedGame}
            slotDisabled={selectedGame && previewSlots ? slotDisabled : () => true}
            slotDetail={selectedGame && previewSlots ? slotDetail : undefined}
            slotClassName={selectedGame && previewSlots ? slotClassName : undefined}
            partySizeMin={partySizeMin}
            partySizeMax={partySizeMax}
            dateTestId="date-picker"
            durationTestId="duration-select"
            partySizeTestId="party-size"
          />

          {selectedDate && selectedGame && availability && previewSlots ? (
            <div className="flex items-center gap-4 text-[10px] text-[#52525B] flex-wrap">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#2D8B70]/60 inline-block" />{t('reservations.available')}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#F59E0B]/60 inline-block" />{text('Última unidad', 'Last unit')}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#EF4444]/60 inline-block" />{text('Agotado', 'Full')}</span>
            </div>
          ) : null}

          {selectedSlot && (
            <div className="space-y-4">
              {tableSuggestion && (
                <div className={`p-3 rounded-xl border ${tableSuggestion.can_accommodate ? 'bg-[#2D8B70]/10 border-[#2D8B70]/30' : 'bg-[#C45B4A]/10 border-[#C45B4A]/30'}`}>
                  {tableSuggestion.can_accommodate ? (
                    <div className="flex items-start gap-2">
                      <CheckCircle size={16} className="text-[#2D8B70] mt-0.5" />
                      <div>
                        <p className="text-sm text-[#F5F0E6] font-semibold">{text('Mesa asignada automáticamente', 'Table auto-assigned')}</p>
                        <p className="text-xs text-[#A8B5AE]">
                          {tableSuggestion.suggestions[0]?.tables.map(t => `${getLocalizedField(t, 'name', lang, t.name)} (${t.capacity}p)`).join(', ')}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <AlertCircle size={16} className="text-[#C45B4A] mt-0.5" />
                      <div>
                        <p className="text-sm text-[#F5F0E6] font-semibold">{text('Sin mesas disponibles', 'No tables available')}</p>
                        <p className="text-xs text-[#A8B5AE]">{text('Prueba otra hora o reduce el grupo', 'Try another time or reduce party size')}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: Confirm */}
        <div className="space-y-6">
          <div className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-2xl p-6">
            <h3 className="text-[#F5F0E6] font-bold mb-4">{text('Resumen', 'Summary')}</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-[#A8B5AE]">{text('Juego', 'Game')}</span><span className="text-[#F5F0E6] truncate max-w-[130px]">{selectedGame ? getLocalizedField(selectedGame, 'name', lang, selectedGame.name) : '-'}</span></div>
              <div className="flex justify-between"><span className="text-[#A8B5AE]">{t('reservations.select_date')}</span><span className="text-[#F5F0E6]">{selectedDate || '-'}</span></div>
              <div className="flex justify-between"><span className="text-[#A8B5AE]">{t('reservations.select_slot')}</span><span className="text-[#F5F0E6]">{selectedSlot || '-'}</span></div>
              <div className="flex justify-between"><span className="text-[#A8B5AE]">{t('reservations.duration')}</span><span className="text-[#F5F0E6]">{duration}h</span></div>
              <div className="flex justify-between"><span className="text-[#A8B5AE]">{t('reservations.party_size')}</span><span className="text-[#F5F0E6]">{partySize} {text('personas', 'people')}</span></div>
              {tableSuggestion?.can_accommodate && (
                <div className="flex justify-between"><span className="text-[#A8B5AE]">{text('Mesas', 'Tables')}</span><span className="text-[#2D8B70]">{tableSuggestion.suggestions[0]?.tables.length} mesa(s)</span></div>
              )}
            </div>
            {error && <p className="text-[#C45B4A] text-sm mt-4 bg-[#C45B4A]/10 px-3 py-2 rounded-lg">{error}</p>}
            {success && <p className="text-[#2D8B70] text-sm mt-4 bg-[#2D8B70]/10 px-3 py-2 rounded-lg">{success}</p>}
            <button onClick={handleConfirm} disabled={loading || !selectedGame || !selectedDate || !selectedSlot || (tableSuggestion && !tableSuggestion.can_accommodate)}
              className="w-full btn-primary py-3 mt-4 disabled:opacity-40 text-sm"
              data-testid="confirm-reservation-btn">
              {loading ? '...' : (user ? t('reservations.confirm') : text('Iniciar sesión para reservar', 'Login to reserve'))}
            </button>
          </div>
        </div>
      </div>

      {/* My Reservations */}
      {user && myReservations.length > 0 && (
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-[#F5F0E6] mb-6" style={{ fontFamily: 'Cinzel, serif' }}>{t('reservations.my_reservations')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {myReservations.map(res => (
              <div key={res.reservation_id} className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl p-4" data-testid={`reservation-${res.reservation_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <h4 className="text-[#F5F0E6] font-semibold text-sm">{res.game_name}</h4>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${res.status === 'confirmed' ? 'status-available' : 'status-booked'}`}>
                    {res.status === 'confirmed' ? t('reservations.confirmed') : t('reservations.cancelled')}
                  </span>
                </div>
                <div className="space-y-1 text-xs text-[#A8B5AE]">
                  <div className="flex items-center gap-1"><Calendar size={11} />{res.date}</div>
                  <div className="flex items-center gap-1"><Clock size={11} />{res.time_slot} ({res.duration_hours}h)</div>
                  <div className="flex items-center gap-1"><Users size={11} />{res.party_size || 2} {text('personas', 'people')}</div>
                </div>
                {/* Linked Tables */}
                {res.linked_tables && res.linked_tables.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-[#C9A962]/20">
                    <p className="text-xs text-[#C9A962] flex items-center gap-1">
                      <Link2 size={10} />
                      {res.linked_tables.map(t => `Mesa ${t.table_number}`).join(', ')}
                    </p>
                  </div>
                )}
                {res.status === 'confirmed' && (
                  <button onClick={() => handleCancel(res.reservation_id)}
                    className="mt-3 w-full text-xs text-[#C45B4A] bg-[#C45B4A]/10 hover:bg-[#C45B4A]/20 rounded-lg py-1.5 transition-colors"
                    data-testid={`cancel-res-${res.reservation_id}`}>{t('reservations.cancel')}</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
